import type { ApiClient, Recording } from "./api";
import { useLoaded } from "./useLoaded";

/** How often the list asks again while anything is still moving. */
const POLL_MS = 2000;

export interface RecordingsState {
  recordings: Recording[];
  /** True until the first answer arrives, so a list can tell "empty" from "not yet". */
  loading: boolean;
  /** Why the list could not be read, or null. */
  problem: string | null;
  refresh: () => void;
}

const readRecordings = (api: ApiClient) => api.listRecordings();

const pollWhileMoving = (recordings: Recording[]) =>
  recordings.some((recording) => recording.status === "queued" || recording.status === "processing")
    ? POLL_MS
    : null;

/**
 * UI-4's live status.
 *
 * Polling, and deliberately the simplest kind: the Server is asked again every couple
 * of seconds while at least one recording is queued or processing, and not at all
 * otherwise. A finished list therefore costs nothing to leave open, and there is no
 * socket to keep alive through a home router.
 *
 * It lives at the top of the app rather than inside the page because the navigator
 * panel shows the same rows with the same status dots — two copies would be two lists
 * disagreeing about the same recording for a second or two at a time.
 */
export function useRecordings(api: ApiClient): RecordingsState {
  const { value, loading, problem, refresh } = useLoaded(
    api,
    readRecordings,
    [] as Recording[],
    "The recordings could not be read.",
    { poll: pollWhileMoving },
  );

  return { recordings: value, loading, problem, refresh };
}
